import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Heart, Sparkles, Package } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/a9zmkl5k_IMG_0819.jpeg";
const LOGO = "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/ro8wmkw2_Logo%20Confeitaria%20Divertido%20Rosa%20e%20Bege.png";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const featured = products.filter(p => p.category !== "kits").slice(0, 4);
  const kits = products.filter(p => p.category === "kits");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-cream-deep border border-cocoa/10 px-4 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5 text-rose-accent" />
              <span className="text-xs uppercase tracking-widest text-cocoa-soft">Pequenos lotes · feito à mão</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-cocoa-dark leading-[1.05] mt-6">
              Salgados e brigadeiros
              <span className="block font-accent text-rose-accent text-4xl sm:text-5xl mt-2">feitos com amor</span>
            </h1>
            <p className="text-cocoa-soft text-lg mt-6 max-w-xl leading-relaxed">
              Caixas artesanais com a memória de casa. Encomende para festas, mimos ou simplesmente porque o dia merece.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/catalogo"
                data-testid="hero-cta-shop"
                className="inline-flex items-center gap-2 bg-cocoa-dark hover:bg-cocoa text-cream px-7 py-3.5 rounded-full font-medium transition-colors"
              >
                Ver catálogo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/sobre"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 bg-cream-alt hover:bg-cream-deep text-cocoa-dark px-7 py-3.5 rounded-full border border-cocoa/10 transition-colors"
              >
                A nossa história
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat number="16+" label="Sabores" />
              <Stat number="20g" label="Salgados" />
              <Stat number="3" label="Tamanhos brigadeiro" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={HERO_IMG} alt="Brigadeiros artesanais" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-cocoa-dark/30 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-cream rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-cocoa/10">
              <img src={LOGO} className="w-14 h-14 rounded-full" alt="Caseirinhos" />
              <div>
                <div className="font-heading text-xl text-cocoa-dark leading-none">Caseirinhos</div>
                <div className="text-xs uppercase tracking-widest text-rose-accent mt-1">Desde sempre · com amor</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          <CategoryCard
            to="/catalogo?cat=salgados"
            title="Salgados"
            tagline="20g · fritos e crocantes"
            image="https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/mim45qtq_45D2AA2D-A772-444E-9D22-499CC5B00016.jpeg"
            testid="cat-salgados"
          />
          <CategoryCard
            to="/catalogo?cat=brigadeiros"
            title="Brigadeiros"
            tagline="12g · 16g · 22g"
            image="https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/1po11hb3_EADF2D61-6A6E-4630-9CE9-0D15DF5A0DAA.jpeg"
            testid="cat-brigadeiros"
          />
        </div>
      </section>

      {/* KITS */}
      {kits.length > 0 && (
        <section data-testid="home-kits" className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
          <div className="bg-rose/15 border border-rose/40 rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-rose/30 blur-3xl" />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-xs uppercase tracking-widest text-rose-accent">Packs prontos</p>
                <h2 className="font-heading text-4xl sm:text-5xl text-cocoa-dark mt-2">Kits Caseirinhos</h2>
                <p className="text-cocoa-soft mt-3 max-w-xl">Sabor combinado, preço pensado. A escolha mais simples para festas, encontros e celebrações.</p>
              </div>
              <Link to="/catalogo?cat=kits" data-testid="home-kits-cta" className="self-start lg:self-end inline-flex items-center gap-2 bg-cocoa-dark hover:bg-cocoa text-cream px-6 py-3 rounded-full text-sm transition-colors">
                Ver todos os kits <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative z-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {kits.slice(0, 3).map((k, i) => <ProductCard key={k.id} product={k} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-rose-accent">Os preferidos da casa</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-cocoa-dark mt-2">Em destaque</h2>
          </div>
          <Link to="/catalogo" className="hidden sm:flex items-center gap-2 text-cocoa-soft hover:text-rose-accent text-sm uppercase tracking-widest transition-colors">
            Ver tudo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-cream-deep py-20 mt-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid md:grid-cols-3 gap-10">
          <Value icon={Heart} title="Receitas da família" desc="Recolhidas ao longo dos anos, com tempo e carinho." />
          <Value icon={Package} title="Caixas para presentear" desc="Embalagens cuidadas para ofertas e festas." />
          <Value icon={Sparkles} title="Sempre frescos" desc="Preparados sob encomenda em pequenos lotes." />
        </div>
      </section>

      {/* PICKUP INFO */}
      <section data-testid="pickup-info" className="max-w-5xl mx-auto px-6 sm:px-8 py-20">
        <div className="bg-cream rounded-3xl border border-cocoa/10 p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-mint/30 blur-3xl" />
          <div className="relative z-10 grid lg:grid-cols-2 gap-10">
            <div>
              <p className="text-xs uppercase tracking-widest text-rose-accent">Como encomendar</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-cocoa-dark mt-2 leading-tight">Encomenda com calma,<br/>levanta na hora.</h2>
              <p className="text-cocoa-soft mt-5 leading-relaxed">
                Para garantir o melhor sabor e a melhor preparação, todas as encomendas devem ser feitas com
                <strong className="text-cocoa-dark"> antecedência mínima de 2 dias</strong>. Após preencher o pedido,
                abrimos uma conversa no WhatsApp para combinar o sinal de 50% que confirma a encomenda.
              </p>
              <p className="text-cocoa-soft mt-3 leading-relaxed">Os restantes 50% são pagos no levantamento.</p>
            </div>
            <div>
              <h3 className="font-heading text-2xl text-cocoa-dark mb-4">Horários de levantamento</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between border-b border-cocoa/10 pb-2"><span className="text-cocoa-soft">Segunda a sexta-feira</span><span className="text-cocoa-dark font-medium">9h às 19h</span></li>
                <li className="flex justify-between border-b border-cocoa/10 pb-2"><span className="text-cocoa-soft">Sábado</span><span className="text-cocoa-dark font-medium">8h às 14h</span></li>
                <li className="flex justify-between border-b border-cocoa/10 pb-2"><span className="text-cocoa-soft">Domingo</span><span className="text-cocoa-dark font-medium">8h às 12h</span></li>
                <li className="flex justify-between"><span className="text-cocoa-soft">Feriados</span><span className="text-cocoa-dark font-medium">8h às 12h</span></li>
              </ul>
              <div className="mt-6 bg-cream-deep rounded-xl p-4 text-sm">
                <p className="text-xs uppercase tracking-widest text-rose-accent">Local de levantamento</p>
                <p className="text-cocoa-dark mt-1 font-medium">Rua António Duarte Caneças, nº 1</p>
                <p className="text-cocoa-dark">2700-069 Amadora</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-heading text-3xl text-cocoa-dark">{number}</div>
      <div className="text-xs uppercase tracking-widest text-rose-accent mt-1">{label}</div>
    </div>
  );
}

function CategoryCard({ to, title, tagline, image, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group relative aspect-[4/3] rounded-2xl overflow-hidden lift block">
      <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-cocoa-dark/80 via-cocoa-dark/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-8 text-cream">
        <p className="text-xs uppercase tracking-widest text-rose">{tagline}</p>
        <h3 className="font-heading text-4xl mt-2 text-cream">{title}</h3>
        <span className="inline-flex items-center gap-2 mt-3 text-sm">
          Explorar <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function Value({ icon: Icon, title, desc }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose/30 text-cocoa-dark mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-heading text-2xl text-cocoa-dark">{title}</h3>
      <p className="text-cocoa-soft mt-2 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
